import React, { FunctionComponent, useEffect, useState } from "react";
import { T } from "services/lang/LanguageService";
import { DweenTimeUnitEnum } from "utils/enums";
import { Divider } from "primereact/divider";
import { Editor } from "@monaco-editor/react";
import TimeUnitDropdown from "components/basic/dropdowns/time-unit-dropdown/TimeUnitDropdown";
import { Button } from "primereact/button";
import { DropdownChangeEvent } from "primereact/dropdown";
import { InputNumber, InputNumberValueChangeEvent } from "primereact/inputnumber";
import { InputSwitch, InputSwitchChangeEvent } from "primereact/inputswitch";
import { ITableConfigProp } from "../common/api/interfaces/interfaces";
import DatasetConfigEditor from "./dataset-config-editor/DatasetConfigEditor";

const defaultDataInputOutputTableConfig: ITableConfigProp = {
  config: {
    alignStartTo: DweenTimeUnitEnum.HOURS,
    timeOffset: 0,
    timeOffsetUnits: DweenTimeUnitEnum.HOURS,
    dataFrequency: DweenTimeUnitEnum.HOURS,
    updateFrequency: 5,
    maxHeight: 450,
  },
  datasets: [],
};

type DataInputOutputTableEditorProps = {
  config?: ITableConfigProp;
  onConfigChange: (config: ITableConfigProp) => void;
};

function DataInputOutputTableEditor({ config, onConfigChange }: DataInputOutputTableEditorProps) {
  let conf: ITableConfigProp = config ?? defaultDataInputOutputTableConfig;

  if (Object.keys(conf).length === 0) {
    conf = defaultDataInputOutputTableConfig;
  }

  const [currentConfig, setCurrentConfig] = useState<ITableConfigProp>(conf);
  const [datasetConfigEditorIsVisible, setDatasetConfigEditorVisibility] = useState(false);

  useEffect(() => {
    onConfigChange(currentConfig);
  }, [currentConfig]);

  const handleDatasetConfigEditClick = () => {
    setDatasetConfigEditorVisibility(true);
  };

  const handleConfigChange = (e: InputNumberValueChangeEvent | DropdownChangeEvent) => {
    setCurrentConfig({
      ...currentConfig,
      config: {
        ...currentConfig.config,
        [e.target.id]: e.target.value?.toString().toUpperCase(),
      },
    });
  };

  const handleInputSwitchChange = (e: InputSwitchChangeEvent) => {
    setCurrentConfig({
      ...currentConfig,
      config: {
        ...currentConfig.config,
        [e.target.id]: e.target.value,
      },
    });
  };

  const handleNoteChange = (value?: string) => {
    setCurrentConfig({
      ...currentConfig,
      config: {
        ...currentConfig.config,
        note: value,
      },
    });
  };

  return (
    <div>
      <div className="grid p-fluid">
        <div className="flex flex-row w-12 justify-content-between">
          <Button
            icon="pi pi-list"
            type="button"
            label="Datasets list"
            className="component-editor-button icon-blue w-2 align-self-center"
            onClick={() => handleDatasetConfigEditClick()}
          />
          <div className="flex flex-row gap-2 w-4">
            <div className="w-6 gap-2 flex flex-column align-items-center">
              <label htmlFor="displayNote">{T("action.add-note")}</label>
              <InputSwitch
                id="displayNote"
                checked={Boolean(currentConfig.config?.displayNote)}
                onChange={handleInputSwitchChange}
              />
            </div>
            <div className="w-6 gap-2 flex flex-column align-items-center">
              <label htmlFor="hideUpdateButton">{T("action.hide-update-button")}</label>
              <InputSwitch
                id="hideUpdateButton"
                checked={Boolean(currentConfig.config?.hideUpdateButton)}
                onChange={handleInputSwitchChange}
              />
            </div>
          </div>
          <DatasetConfigEditor
            currentConfig={currentConfig}
            setCurrentConfig={setCurrentConfig}
            onConfigChange={onConfigChange}
            datasetConfigEditorIsVisible={datasetConfigEditorIsVisible}
            setDatasetConfigEditorVisibility={setDatasetConfigEditorVisibility}
          />
        </div>
        <Divider />
        <div className="flex w-12">
          <div className="col-4 gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="timeOffset">{T("load-conf.time-offset")}</label>
            <div className="p-inputgroup">
              <InputNumber
                id="timeOffset"
                className="w-6"
                value={currentConfig.config?.timeOffset as number}
                onValueChange={handleConfigChange}
              />
              <TimeUnitDropdown
                id="timeOffsetUnits"
                className="p-inputgroup-addon p-0 w-6"
                value={currentConfig.config?.timeOffsetUnits ? currentConfig.config.timeOffsetUnits : ""}
                onChange={handleConfigChange}
              />
            </div>
          </div>
          <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="dataFrequency">{T("config.data-frequency")}</label>
            <TimeUnitDropdown
              id="dataFrequency"
              className="p-0"
              value={currentConfig.config?.dataFrequency ? currentConfig.config.dataFrequency : ""}
              onChange={handleConfigChange}
            />
          </div>
          <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="alignStartTo">{T("load-conf.align-start-to")}</label>
            <TimeUnitDropdown
              id="alignStartTo"
              className="p-0"
              value={currentConfig.config?.alignStartTo ? currentConfig.config.alignStartTo : ""}
              onChange={handleConfigChange}
            />
          </div>
          <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="updateFrequency">{T("load-conf.update-frequency")}</label>
            <div className="p-inputgroup w-full">
              <InputNumber
                id="updateFrequency"
                value={currentConfig.config?.updateFrequency as number}
                onValueChange={handleConfigChange}
              />
              <span className="p-inputgroup-addon">s</span>
            </div>
          </div>
          <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="maxHeight">{T("config.max-height")}</label>
            <div className="p-inputgroup w-full">
              <InputNumber
                id="maxHeight"
                value={currentConfig.config?.maxHeight as number}
                onValueChange={handleConfigChange}
              />
              <span className="p-inputgroup-addon">px</span>
            </div>
          </div>
        </div>
        {currentConfig.config?.displayNote && (
          <>
            <Divider />
            <div className="w-12 flex flex-column gap-2">
              <label htmlFor="note">{T("word.note")}</label>
              <Editor
                height="30rem"
                value={currentConfig.config?.note}
                onChange={handleNoteChange}
                defaultLanguage="html"
                className="border-1 border-300 border-round overflow-hidden"
                options={{
                  lineNumbers: "off",
                  lineDecorationsWidth: 0,
                  minimap: {
                    enabled: false,
                  },
                }}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default DataInputOutputTableEditor as FunctionComponent;
